import {
  EAgentToolType,
  EAiModelStatus,
  ECrewAiAgentMemoryMode,
  EDatasourceType,
  IAiModel,
  ICommonSettings,
  ICrewAgent,
  ICrewAgentTool,
  ICrewAiModelConfig,
  ICrewCustomDataType,
  ICrewInput,
  ICrewManagerAgent,
  ICrewMemoryOptions
} from "@kuika/kuika-cl-model";
import _ from "lodash";
import * as React from "react";
import { DatasourceService } from "../../../../services/datasource-service";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import KuikaBadge from "../../../ui-kit/kuika-badge/kuika-badge";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaCollapsePanel from "../../../ui-kit/kuika-collapse-panel/kuika-collapse-panel";
import { copyTextToClipboard } from "../../../ui-kit/kuika-copy-button/copy-text-to-clipboard";
import KuikaDropdownMenu, { KuikaDropdownMenuItemType } from "../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaEmptyState from "../../../ui-kit/kuika-empty-state/kuika-empty-state";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSwitch from "../../../ui-kit/kuika-switch/kuika-switch";
import KuikaTooltip from "../../../ui-kit/kuika-tooltip/kuika-tooltip";
import {
  AGENT_TOOLS,
  formatActionDisplayName,
  getActionCategoryBadge,
  getMcpConfigOptions,
  IAgentToolOption
} from "./ai-agent-helper";
import "./ai-agent-sidebar.scss";

const TYPE_OPTIONS = [
  { value: "str", label: "string" },
  { value: "bool", label: "boolean" },
  { value: "float", label: "float" },
  { value: "int", label: "integer" }
];

export interface IAIAgentSidebarProps {
  inputs: ICrewInput[];
  agents?: ICrewAgent[];
  customDataTypes?: ICrewCustomDataType[];
  processType?: "sequential" | "hierarchical";
  managerAgent?: ICrewManagerAgent;
  processManagerReadOnly?: boolean;
  selectedAgent?: ICrewAgent;
  isManagerMode?: boolean;
  viewMode?: "edit" | "preview";
  onProcessTypeChange?: (processType: "sequential" | "hierarchical") => void;
  onManagerAgentChange?: (managerAgent?: ICrewManagerAgent) => void;
  onInputsChange?: (inputs: ICrewInput[]) => void;
  onCustomDataTypesChange?: (customDataTypes: ICrewCustomDataType[]) => void;
  onOpenInputsModal?: () => void;
  onOpenCustomTypesModal?: () => void;
  onOpenResponseModal?: () => void;
  onOpenManagerResponseModal?: () => void;
  onOpenMcpConfig?: (toolIndex: number) => void;
  onCloseMcpConfig?: () => void;
  commonSettings?: ICommonSettings;
  activeMcpToolIndex?: number | null;
  onMcpSettingsSave?: (toolIndex: number, settingsId: string | undefined) => void;
  onOpenMcpAddConfig?: (mcpTool: ICrewAgentTool) => void;
  onClearSelection?: () => void;
  onAgentUpdate?: (agent: ICrewAgent) => void;
  onEditAgentName?: () => void;
  onRemoveAgent?: () => void;
  preferredAccordionKey?: string;
  // Crew-level memory options. Owns the embedder used by *shared* crew memory.
  crewMemoryOptions?: ICrewMemoryOptions;
  onCrewMemoryOptionsChange?: (memoryOptions?: ICrewMemoryOptions) => void;
  // KOA-16736: KAction tools
  kActionOptions?: IAgentToolOption[];
  // Active KAction (set when user clicks a KAction tool node on the workflow canvas).
  // When set, the entire sidebar is replaced with the action-detail view — same UX
  // pattern as activeMcpToolIndex / renderMcpToolConfigView.
  activeKActionId?: string | null;
  onCloseKActionDetail?: () => void;
  conversationHistoryEnabled?: boolean;
  onConversationHistoryChange?: (enabled: boolean) => void;
}

export interface IAIAgentSidebarState {
  activeAccordionKeys: string[];
  expandedCustomTypeKeys: string[];
  llmModels?: IAiModel[];
  fcLlmModels?: IAiModel[];
  embedderModels?: IAiModel[];
  copiedFieldKey?: string | null;
  // KOA-16736: KAction tool detail editor state (active id comes from props now)
  editingDescriptionDraft?: string;
  isGeneratingDescription?: boolean;
  isSavingDescription?: boolean;
}

class AIAgentSidebar extends React.PureComponent<IAIAgentSidebarProps, IAIAgentSidebarState> {
  private readonly PROVIDER_NAMES: Record<string, string> = {
    "4D9AC0E3-D3EE-49D1-B0F6-813D9A7C237F": "OpenAI",
    "876905BF-0E2C-4D20-84D1-D2E3B5FAFDA1": "Google"
  };

  constructor(props: IAIAgentSidebarProps) {
    super(props);
    const normalizedKey = props.preferredAccordionKey?.replace(/_$/, "");
    this.state = {
      activeAccordionKeys:
        normalizedKey === "allAgentSections"
          ? ["crewProcess", "selectedAgent", "tools", "actions", "agentParams", "returnTypes", "inputs", "customTypes"]
          : normalizedKey
            ? [normalizedKey]
            : ["selectedAgent"],
      expandedCustomTypeKeys: [],
      llmModels: undefined,
      fcLlmModels: undefined,
      embedderModels: undefined
    };
  }

  handleProcessTypeChange = (value: "sequential" | "hierarchical") => {
    this.props.onProcessTypeChange?.(value);
  };

  handleManagerAgentFieldChange = (
    field: keyof ICrewManagerAgent | string,
    value: string | boolean | number | undefined
  ) => {
    const currentManagerAgent = _.cloneDeep(this.props.managerAgent || {}) as any;

    if (typeof field === "string" && field.startsWith("task.")) {
      currentManagerAgent.task = currentManagerAgent.task || {};
      const taskField = field.slice("task.".length);

      if (value === undefined || value === "") {
        delete currentManagerAgent.task[taskField];
      } else {
        currentManagerAgent.task[taskField] = value;
      }

      if (currentManagerAgent.task && Object.keys(currentManagerAgent.task).length === 0) {
        delete currentManagerAgent.task;
      }
    } else if (value === undefined || value === "") {
      delete currentManagerAgent[field];
    } else {
      currentManagerAgent[field] = value;
    }

    const hasValues = Object.keys(currentManagerAgent).length > 0;
    this.props.onManagerAgentChange?.(hasValues ? currentManagerAgent : undefined);
  };

  private getBooleanDropdownOptions = (): KuikaDropdownMenuItemType[] => {
    return [
      { key: "", value: "", label: "Not set", showSelectedIcon: false },
      { key: "true", value: "true", label: "True" },
      { key: "false", value: "false", label: "False" }
    ];
  };

  private getMemoryModeDropdownOptions = (): KuikaDropdownMenuItemType[] => {
    return [
      {
        key: String(ECrewAiAgentMemoryMode.Disabled),
        value: String(ECrewAiAgentMemoryMode.Disabled),
        label: "Disabled"
      },
      {
        key: String(ECrewAiAgentMemoryMode.Shared),
        value: String(ECrewAiAgentMemoryMode.Shared),
        label: "Shared (crew memory)"
      },
      {
        key: String(ECrewAiAgentMemoryMode.Isolated),
        value: String(ECrewAiAgentMemoryMode.Isolated),
        label: "Isolated (own store)"
      }
    ];
  };

  // Default embedder (matches backend CrewAiMemoryOptions defaults).
  private static readonly DEFAULT_EMBEDDER_NAME = "text-embedding-3-small";
  private static readonly OPENAI_PROVIDER_ID = "4D9AC0E3-D3EE-49D1-B0F6-813D9A7C237F";

  // Resolves the AI model that corresponds to a saved embedder config. The persisted
  // shape is { name, providerId } (not an id), so we match by id first (future-proof),
  // then name.
  private resolveEmbedderModel = (embedder?: ICrewAiModelConfig): IAiModel | undefined => {
    if (!embedder) return undefined;
    const models = this.state.embedderModels || [];
    if (embedder.id) {
      const byId = models.find((m) => m.id.toLowerCase() === embedder.id!.toLowerCase());
      if (byId) return byId;
    }
    if (embedder.name) {
      const byName = models.find((m) => m.name.toLowerCase() === embedder.name!.toLowerCase());
      if (byName) return byName;
    }
    return undefined;
  };

  private buildEmbedderConfigFromModelId = (modelId: string): ICrewAiModelConfig | undefined => {
    const model = (this.state.embedderModels || []).find((m) => m.id === modelId);
    if (!model) return undefined;
    return {
      id: model.id,
      name: model.name,
      providerId: (model as any).providerId || undefined
    };
  };

  handleCrewEmbedderChange = (modelId: string) => {
    const { crewMemoryOptions, onCrewMemoryOptionsChange } = this.props;
    if (!onCrewMemoryOptionsChange) return;

    if (!modelId) {
      if (!crewMemoryOptions?.embedder) return;
      const { embedder: _omit, ...rest } = crewMemoryOptions;
      onCrewMemoryOptionsChange(Object.keys(rest).length ? rest : undefined);
      return;
    }

    const embedder = this.buildEmbedderConfigFromModelId(modelId);
    if (!embedder) return;
    onCrewMemoryOptionsChange({ ...(crewMemoryOptions || {}), embedder });
  };

  private memoryModeLabel = (mode: ECrewAiAgentMemoryMode | undefined): string => {
    switch (mode) {
      case ECrewAiAgentMemoryMode.Shared:
        return "Shared (crew memory)";
      case ECrewAiAgentMemoryMode.Isolated:
        return "Isolated (own store)";
      case ECrewAiAgentMemoryMode.Disabled:
        return "Disabled";
      default:
        return "Not set";
    }
  };

  private getProviderIcon = (model: any): string => {
    const providerId = model?.providerId;
    if (!providerId) return "";
    const normalizedId = String(providerId).toUpperCase();
    if (normalizedId === "4D9AC0E3-D3EE-49D1-B0F6-813D9A7C237F") return "openai";
    if (normalizedId === "876905BF-0E2C-4D20-84D1-D2E3B5FAFDA1") return "gemini";
    return "";
  };

  private getModelDropdownOptions = (
    selectedValue: string,
    selectedLabel: string,
    placeholder: string,
    modelList?: IAiModel[]
  ): KuikaDropdownMenuItemType[] => {
    const options: KuikaDropdownMenuItemType[] = [];
    // Only apply the InstructionModel (category 0) filter when falling back to the shared
    // llmModels list. Explicit lists (fcLlmModels, embedderModels) come from dedicated
    // backend endpoints that are already category-scoped.
    const filterByInstructionCategory = modelList === undefined;
    const source = modelList ?? this.state.llmModels ?? [];
    const seenIds = new Set<string>();
    const activeModels = source.filter((model) => {
      if (model.status !== EAiModelStatus.Active) return false;
      if (filterByInstructionCategory && !(model.categories || []).includes(0)) return false;
      const key = model.id.toLowerCase();
      if (seenIds.has(key)) return false;
      seenIds.add(key);
      return true;
    });

    if (!selectedValue) {
      options.push({
        key: "",
        value: "",
        label: placeholder,
        showSelectedIcon: false
      });
    }

    const selectedLower = selectedValue.toLowerCase();
    const selectedInList = activeModels.some(
      (model) => model.id.toLowerCase() === selectedLower || model.name.toLowerCase() === selectedLower
    );
    if (selectedValue && !selectedInList) {
      options.push({
        key: selectedValue,
        value: selectedValue,
        label: selectedLabel
      });
    }

    activeModels.forEach((model) => {
      const iconName = this.getProviderIcon(model);
      options.push({
        key: model.id,
        value: model.id,
        label: this.formatModelLabel(model.name),
        group: this.getProviderName(model),
        icon: iconName ? (
          <KuikaSvg type="agent-icons" name={iconName} width={12} height={12} useOriginalColors />
        ) : undefined
      });
    });

    return options;
  };

  private getProviderName = (model: any): string => {
    const providerId = model?.providerId;
    if (!providerId) {
      return "Other";
    }

    const normalizedId = String(providerId).toUpperCase();
    return this.PROVIDER_NAMES[normalizedId] || "Other";
  };

  private renderDropdownField = (
    selectedValue: string,
    displayValue: string,
    options: KuikaDropdownMenuItemType[],
    onSelect: (value: string) => void,
    isDisabled = false
  ) => {
    const trigger = (
      <button
        type="button"
        className={`ai-agent-params__agent-input ai-agent-params__dropdown-trigger${
          isDisabled ? " ai-agent-params__dropdown-trigger--disabled" : ""
        }`}
        disabled={isDisabled}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}
        >
          <span className="ai-agent-params__dropdown-trigger-label">{displayValue}</span>
          <KuikaSvg type="arrows" name="chevron-down" width={14} height={14} />
        </div>
      </button>
    );

    if (isDisabled) {
      return trigger;
    }

    return (
      <KuikaDropdownMenu
        options={options}
        selected={selectedValue}
        setSelected={(value) => onSelect(value === null || value === undefined ? "" : String(value))}
        trigger="click"
        placement="bottomLeft"
        getPopupContainer={() => document.body}
        overlayClassName="ai-agent-params__dropdown-menu"
      >
        {trigger}
      </KuikaDropdownMenu>
    );
  };

  renderCrewProcessSettings = () => {
    const { processType = "sequential", managerAgent, viewMode, processManagerReadOnly } = this.props;
    const isEditable = viewMode === "edit" && !processManagerReadOnly;
    const isHierarchical = processType === "hierarchical";
    const managerModelId = managerAgent?.model || "";
    const managerModelIdLower = managerModelId.toLowerCase();
    const resolvedManagerModel = managerModelId
      ? this.state.llmModels?.find((m) => m.id.toLowerCase() === managerModelIdLower) ||
        this.state.llmModels?.find((m) => m.name.toLowerCase() === managerModelIdLower)
      : undefined;
    const managerModelSelectValue = resolvedManagerModel?.id || managerModelId;
    const managerModelLabel = resolvedManagerModel
      ? this.formatModelLabel(resolvedManagerModel.name)
      : managerModelId
        ? "Unknown Model"
        : "Select a model";
    const isResolvingManagerModel = this.state.llmModels === undefined && !!managerModelId;

    return (
      <div className="ai-agent-params__section">
        {processManagerReadOnly && (
          <div className="ai-agent-params__lock-note">
            Template-origin agents use locked process and manager settings.
          </div>
        )}

        <div className="ai-agent-params__agent-section ai-agent-params__agent-section--inline">
          <div className="ai-agent-params__agent-label">Process Type</div>
          <div className="ai-agent-params__agent-control">
            <KuikaTooltip content="Process type cannot be changed after creation">
              <div className="ai-agent-params__param-value">
                {processType === "hierarchical" ? "Hierarchical" : "Sequential"}
              </div>
            </KuikaTooltip>
          </div>
        </div>

        {isHierarchical && (
          <>
            <div className="ai-agent-params__agent-section ai-agent-params__agent-section--inline">
              <div className="ai-agent-params__agent-label">Conversation History</div>
              <div className="ai-agent-params__agent-control">
                {isEditable ? (
                  this.renderDropdownField(
                    this.props.conversationHistoryEnabled ? "1" : "0",
                    this.props.conversationHistoryEnabled ? "Enabled" : "Disabled",
                    [
                      { key: "0", label: "Disabled", value: "0" },
                      { key: "1", label: "Enabled", value: "1" }
                    ],
                    (value) => this.props.onConversationHistoryChange?.(value === "1")
                  )
                ) : (
                  <div className="ai-agent-params__param-value">
                    {this.props.conversationHistoryEnabled ? "Enabled" : "Disabled"}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  componentDidMount() {
    const normalize = (data: any[]) =>
      (data || [])
        .filter((m: any) => m.name && m.id)
        .map((m: any) => ({
          ...m,
          providerId: m.providerId || m.ProviderId || null
        }));

    // Fetch available LLM models from backend
    DatasourceService.GetAiModels()
      .then((res) => this.setState({ llmModels: normalize(res.data) }))
      .catch((e) => {
        KMainFunctions.exceptionHandler(e);
      });

    DatasourceService.GetAgentFunctionCallingModels()
      .then((res) => this.setState({ fcLlmModels: normalize(res.data) }))
      .catch((e) => {
        KMainFunctions.exceptionHandler(e);
      });

    DatasourceService.GetAgentEmbeddingModels()
      .then((res) => this.setState({ embedderModels: normalize(res.data) }))
      .catch((e) => {
        KMainFunctions.exceptionHandler(e);
      });
  }

  componentDidUpdate(prevProps: IAIAgentSidebarProps, prevState: IAIAgentSidebarState) {
    if (this.props.preferredAccordionKey && this.props.preferredAccordionKey !== prevProps.preferredAccordionKey) {
      const normalizedKey = this.props.preferredAccordionKey.replace(/_$/, "");
      if (normalizedKey === "allAgentSections") {
        this.setState({
          activeAccordionKeys: [
            "crewProcess",
            "selectedAgent",
            "tools",
            "actions",
            "agentParams",
            "returnTypes",
            "inputs",
            "customTypes"
          ]
        });
      } else if (this.state.activeAccordionKeys[0] !== normalizedKey) {
        this.setState({ activeAccordionKeys: [normalizedKey] });
      } else {
        // Key didn't change content-wise (toggled via _ suffix) — still force open
        this.setState({ activeAccordionKeys: [normalizedKey] });
      }
    }

    // KOA-16736: when the active KAction changes (or closes), reset editor state
    // so we don't carry stale draft text into a different action's view.
    if (prevProps.activeKActionId !== this.props.activeKActionId) {
      this.setState({
        editingDescriptionDraft: undefined,
        isGeneratingDescription: false,
        isSavingDescription: false
      });
    }

    if (prevState.llmModels === this.state.llmModels) {
      return;
    }

    const managerModelValue = this.props.managerAgent?.model;
    if (!managerModelValue || typeof managerModelValue !== "string" || !this.state.llmModels?.length) {
      return;
    }

    const valueLower = managerModelValue.toLowerCase();
    const isKnownId = this.state.llmModels.some((model) => model.id.toLowerCase() === valueLower);
    if (isKnownId) {
      return;
    }

    const modelByName = this.state.llmModels.find((model) => model.name.toLowerCase() === valueLower);
    if (!modelByName) {
      return;
    }

    // Backward compatibility: convert previously stored manager model name to model id.
    this.handleManagerAgentFieldChange("model", modelByName.id);
  }

  private getTypeBadge = (typeName?: string | null): { label: string; className: string } => {
    const defaultBadge = {
      label: typeName || "Unknown",
      className: "ai-agent-params__type-badge--default"
    };

    if (!typeName) {
      return defaultBadge;
    }

    const normalized = typeName.toLowerCase();

    if (normalized.includes("string") || normalized.includes("text") || normalized === "str") {
      return { label: typeName, className: "ai-agent-params__type-badge--text" };
    }

    if (/(int|long|double|float|decimal|number|integer)/.test(normalized)) {
      return { label: typeName, className: "ai-agent-params__type-badge--numeric" };
    }

    if (normalized.includes("bool")) {
      return { label: typeName, className: "ai-agent-params__type-badge--boolean" };
    }

    if (normalized.includes("date") || normalized.includes("time")) {
      return { label: typeName, className: "ai-agent-params__type-badge--datetime" };
    }

    if (normalized.includes("list") || normalized.includes("array") || normalized.includes("collection")) {
      return { label: typeName, className: "ai-agent-params__type-badge--collection" };
    }

    if (normalized.includes("json") || normalized.includes("object") || normalized.includes("model")) {
      return { label: typeName, className: "ai-agent-params__type-badge--object" };
    }

    return defaultBadge;
  };

  private getBadgeColor = (className: string): "blue" | "yellow" | "green" | "purple" | "orange" | "pink" => {
    if (className.includes("text")) return "purple";
    if (className.includes("numeric")) return "orange";
    if (className.includes("boolean")) return "green";
    if (className.includes("datetime")) return "blue";
    if (className.includes("collection")) return "pink";
    if (className.includes("object")) return "yellow";
    return "blue";
  };

  // Extract input names from agent's task description (e.g., {input_name})
  private getAgentInputNames = (): string[] => {
    const { selectedAgent } = this.props;
    if (!selectedAgent?.task?.description) return [];

    const matches = selectedAgent.task.description.match(/\{([^}]+)\}/g) || [];
    return matches.map((match) => match.slice(1, -1)); // Remove { and }
  };

  // Get inputs that are specific to the selected agent
  private getAgentSpecificInputs = (): ICrewInput[] => {
    const { inputs } = this.props;
    const agentInputNames = this.getAgentInputNames();

    if (!inputs || agentInputNames.length === 0) return [];

    return inputs.filter((input) =>
      agentInputNames.some((name) => name.toLowerCase() === input.parameter_name?.toLowerCase())
    );
  };

  parsePropertyValue = (value: string): { datatype: string; description: string | null } => {
    // Parse format: "datatype (description)"
    const match = value.match(/^([^(]+)\s*\(([^)]+)\)$/);
    if (match) {
      return {
        datatype: match[1].trim(),
        description: match[2].trim()
      };
    }
    return {
      datatype: value,
      description: null
    };
  };

  toggleCustomTypeKey = (typeKey: string) => {
    this.setState((prevState) => {
      const expandedCustomTypeKeys = prevState.expandedCustomTypeKeys.includes(typeKey)
        ? prevState.expandedCustomTypeKeys.filter((key) => key !== typeKey)
        : [...prevState.expandedCustomTypeKeys, typeKey];

      return { expandedCustomTypeKeys };
    });
  };

  renderInputRows = (inputs: ICrewInput[]) => {
    return (
      <div className="ai-agent-params__thread-root">
        {inputs.map((input: ICrewInput, index: number) => {
          const typeBadge = this.getTypeBadge(input.parameter_format);

          return (
            <div key={index} className="ai-agent-params__thread-item ai-agent-params__thread--depth-0">
              <div className="ai-agent-params__thread-row">
                <div className="ai-agent-params__thread-row-left">
                  <span className="ai-agent-params__thread-name">{input.parameter_name || "(unnamed)"}</span>
                </div>
                <div className="ai-agent-params__thread-badges">
                  <KuikaBadge content={typeBadge.label} color={this.getBadgeColor(typeBadge.className)} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  renderCustomTypeRows = (customDataTypes: ICrewCustomDataType[], keyPrefix: string) => {
    return (
      <div className="ai-agent-params__thread-root">
        {customDataTypes.map((dataType: ICrewCustomDataType, index: number) => {
          const typeKey = `${keyPrefix}-${index}`;
          const isExpanded = this.state.expandedCustomTypeKeys.includes(typeKey);
          const hasProperties = dataType.properties && Object.keys(dataType.properties).length > 0;

          return (
            <div key={index} className="ai-agent-params__thread-item ai-agent-params__thread--depth-0">
              <div
                className={`ai-agent-params__thread-row ${hasProperties ? "ai-agent-params__thread-row--interactive" : ""}`}
                onClick={() => hasProperties && this.toggleCustomTypeKey(typeKey)}
              >
                <div className="ai-agent-params__thread-row-left">
                  <span className="ai-agent-params__thread-name">
                    {hasProperties && (
                      <KuikaSvg
                        type="arrows"
                        name="chevron-down"
                        width={12}
                        height={12}
                        className={`ai-agent-params__thread-chevron ${
                          isExpanded ? "ai-agent-params__thread-chevron--expanded" : ""
                        }`}
                      />
                    )}
                    {dataType.type_name}
                  </span>
                </div>
                <div className="ai-agent-params__thread-badges">
                  <KuikaBadge content="Object" color="yellow" />
                </div>
              </div>

              {hasProperties && isExpanded && (
                <div className="ai-agent-params__thread-children">
                  {Object.entries(dataType.properties).map(([key, value]: [string, any], propIndex: number) => {
                    const parsed = this.parsePropertyValue(value);
                    const typeBadge = this.getTypeBadge(parsed.datatype);

                    return (
                      <div key={propIndex} className="ai-agent-params__thread-item ai-agent-params__thread--depth-1">
                        <div className="ai-agent-params__thread-row">
                          <div className="ai-agent-params__thread-row-left">
                            <span className="ai-agent-params__thread-name">{key}</span>
                            {parsed.description && (
                              <span className="ai-agent-params__thread-desc">{parsed.description}</span>
                            )}
                          </div>
                          <div className="ai-agent-params__thread-badges">
                            <KuikaBadge content={parsed.datatype} color={this.getBadgeColor(typeBadge.className)} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Format model names similar to ElkNode formatting
  formatModelLabel = (name?: string): string => {
    if (!name || typeof name !== "string") return "Unknown Model";
    return name
      .split("-")
      .map((part, index) => {
        if (index === 0) {
          if (part.toLowerCase() === "gpt") return part.toUpperCase();
          return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
        }
        if (["flash", "mini", "lite", "pro", "nano"].includes(part.toLowerCase())) {
          return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
        }
        return part;
      })
      .join(" ")
      .replace(/\s+/g, " ");
  };

  renderInputs = () => {
    const { inputs } = this.props;

    if (!inputs || inputs.length === 0) {
      return (
        <KuikaEmptyState
          svgName="field"
          customName="No inputs defined"
          description="Add inputs to pass data into this crew at runtime."
        />
      );
    }

    return this.renderInputRows(inputs);
  };

  renderCustomDataTypes = () => {
    const { customDataTypes } = this.props;

    if (!customDataTypes || customDataTypes.length === 0) {
      return (
        <KuikaEmptyState
          svgName="puzzle"
          customName="No custom data types defined"
          description="Define reusable object types that agents can use in their return types."
        />
      );
    }

    return this.renderCustomTypeRows(customDataTypes, "customType");
  };

  renderReturnTypes = (agentOverride?: { task?: { output_format?: Record<string, unknown> } }) => {
    const agent = agentOverride || this.props.selectedAgent;

    if (!agent?.task?.output_format) {
      return (
        <KuikaEmptyState
          svgName="code"
          customName="No return types defined"
          description="Configure a structured output format for this agent in the response modal."
        />
      );
    }

    const outputFormat = agent.task.output_format;
    const hasOutputs = Object.keys(outputFormat).length > 0;

    if (!hasOutputs) {
      return (
        <KuikaEmptyState
          svgName="code"
          customName="No return types defined"
          description="Configure a structured output format for this agent in the response modal."
        />
      );
    }

    return (
      <div className="ai-agent-params__thread-root">
        {Object.entries(outputFormat).map(([key, value], index) => {
          const parsed = this.parsePropertyValue(value as string);
          const typeBadge = this.getTypeBadge(parsed.datatype);

          return (
            <div key={index} className="ai-agent-params__thread-item ai-agent-params__thread--depth-0">
              <div className="ai-agent-params__thread-row" style={{ gap: 0 }}>
                <div className="ai-agent-params__thread-row-left">
                  <span className="ai-agent-params__thread-name" style={{ justifyContent: "space-between" }}>
                    {key}
                    <KuikaBadge content={parsed.datatype} color={this.getBadgeColor(typeBadge.className)} />
                  </span>
                  {parsed.description && <span className="ai-agent-params__thread-desc">{parsed.description}</span>}
                </div>
                <div className="ai-agent-params__thread-badges"></div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  private formatMcpFieldLabel = (key: string): string => {
    const s = key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (c) => c.toUpperCase())
      .trim();
    return s
      .replace(/\bApi\b/g, "API")
      .replace(/\bId\b/g, "ID")
      .replace(/\bUrl\b/g, "URL");
  };

  private isMcpSecretField = (key: string): boolean => {
    const lower = key.toLowerCase();
    return /(secret|apikey|api_key|accesstoken|access_token|clientsecret|client_secret|password|token|appkey|app_key|appsecret|app_secret)/.test(
      lower
    );
  };

  private maskSecretValue = (value: string): string => {
    if (!value || value.length <= 6) return "******";
    const lead = Math.min(3, Math.floor(value.length * 0.2));
    const tail = Math.min(5, Math.floor(value.length * 0.2));
    const stars = "*".repeat(Math.max(6, value.length - lead - tail));
    return value.slice(0, lead) + stars + value.slice(value.length - tail);
  };

  private copyToClipboard = async (value: string, fieldKey: string): Promise<void> => {
    // The shared write (Clipboard API, then the textarea fallback). "Copied!" is said only when
    // it worked — the old path said it on a refused write too.
    const succeeded: boolean = await copyTextToClipboard(value);
    if (!succeeded) return;
    this.setState({ copiedFieldKey: fieldKey });
    setTimeout(() => this.setState({ copiedFieldKey: null }), 2000);
  };

  renderMcpToolConfigView = () => {
    const {
      selectedAgent,
      activeMcpToolIndex,
      commonSettings,
      onMcpSettingsSave,
      onOpenMcpAddConfig,
      onCloseMcpConfig,
      viewMode
    } = this.props;
    if (activeMcpToolIndex == null || !selectedAgent) return null;

    const tool = selectedAgent.tools?.[activeMcpToolIndex];
    if (!tool) return null;

    const toolDef = AGENT_TOOLS.find((t) => t.name === tool.name);
    if (!toolDef?.mcpConfigName) return null;

    const configs = getMcpConfigOptions(commonSettings, toolDef.mcpConfigName);
    const selectedConfig = configs.find((c) => c.id === tool.settingsId);
    const isEditable = viewMode === "edit";

    const configOptions: KuikaDropdownMenuItemType[] = [
      ...(configs || []).map((item) => ({
        key: item.id,
        label: item.name,
        value: item.id,
        onClick: () => onMcpSettingsSave?.(activeMcpToolIndex, item.id)
      })),
      {
        key: "__add-new",
        label: <span style={{ color: "var(--accent-fg)", fontWeight: "500" }}>ADD NEW</span>,
        onClick: () => onOpenMcpAddConfig?.({ name: tool.name, mcpConfigName: toolDef.mcpConfigName! })
      }
    ];

    const triggerLabel = selectedConfig ? selectedConfig.name : "Choose configuration";

    const configFieldEntries = selectedConfig
      ? Object.entries(selectedConfig).filter(([key]) => !["id", "configIds"].includes(key))
      : [];

    return (
      <div className="ai-agent-params">
        <div className="ai-agent-params__mcp-view">
          <div className="ai-agent-params__mcp-view-header">
            <div className="ai-agent-params__mcp-view-icon">
              <KuikaSvg
                type="agent-icons"
                name={toolDef.icon}
                width={24}
                height={24}
                useOriginalColors={!toolDef.icon.endsWith("-tool")}
              />
            </div>
            <span className="ai-agent-params__mcp-view-title">{toolDef.displayName || tool.name}</span>
          </div>

          <div className="ai-agent-params__mcp-view-section">
            <div className="ai-agent-params__mcp-view-row">
              <span className="ai-agent-params__mcp-view-label">Configuration</span>
              <div className="ai-agent-params__mcp-view-control">
                {isEditable ? (
                  <KuikaDropdownMenu
                    options={configOptions}
                    selected={tool.settingsId ?? null}
                    trigger="click"
                    placement="bottomLeft"
                    getPopupContainer={() => document.body}
                    overlayClassName="ai-agent-params__dropdown-menu"
                  >
                    <button type="button" className="ai-agent-params__agent-input ai-agent-params__dropdown-trigger">
                      <span className="ai-agent-params__dropdown-trigger-label">{triggerLabel}</span>
                      <KuikaSvg type="arrows" name="chevron-down" width={12} height={12} color="var(--dark-6)" />
                    </button>
                  </KuikaDropdownMenu>
                ) : (
                  <div className="ai-agent-params__agent-value">{triggerLabel}</div>
                )}
              </div>
            </div>

            {configFieldEntries.map(([key, value]) => {
              const rawValue = String(value ?? "");
              const isSecret = this.isMcpSecretField(key);
              const displayValue = isSecret ? this.maskSecretValue(rawValue) : rawValue;
              const isCopied = this.state.copiedFieldKey === key;
              return (
                <div key={key} className="ai-agent-params__mcp-view-row">
                  <span className="ai-agent-params__mcp-view-label">{this.formatMcpFieldLabel(key)}</span>
                  <div className="ai-agent-params__mcp-view-control">
                    <div
                      className="ai-agent-params__mcp-view-field-value"
                      onClick={() => rawValue && this.copyToClipboard(rawValue, key)}
                      title={rawValue ? (isCopied ? "Copied!" : "Click to copy") : undefined}
                    >
                      <span
                        className={`ai-agent-params__mcp-view-field-text${isSecret ? " ai-agent-params__mcp-view-field-text--secret" : ""}`}
                      >
                        {displayValue}
                      </span>
                      {rawValue && (
                        <span
                          className={`ai-agent-params__mcp-view-field-copy${isCopied ? " ai-agent-params__mcp-view-field-copy--visible" : ""}`}
                        >
                          {isCopied ? (
                            <KuikaSvg type="common" name="check" width={12} height={12} color="var(--green-6)" />
                          ) : (
                            <KuikaSvg type="common" name="copy" width={12} height={12} color="var(--dark-5)" />
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  renderMcpConfigInline = (tool: ICrewAgentTool, toolIndex: number) => {
    const { commonSettings, onMcpSettingsSave, onOpenMcpAddConfig } = this.props;
    const toolDef = AGENT_TOOLS.find((t) => t.name === tool.name);
    if (!toolDef?.mcpConfigName || !commonSettings) return null;

    const configs = getMcpConfigOptions(commonSettings, toolDef.mcpConfigName);
    const currentSettingsId = tool.settingsId;
    const selectedConfig = configs.find((c) => c.id === currentSettingsId);

    const configOptions: KuikaDropdownMenuItemType[] = [
      {
        key: "__add-new",
        // label: "ADD NEW",
        label: <span style={{ color: "var(--accent-fg)", fontWeight: "500" }}>ADD NEW </span>,
        // icon: <KuikaSvg type="common" name="plus" width={12} height={12} />,
        onClick: () =>
          onOpenMcpAddConfig?.({
            name: tool.name,
            mcpConfigName: toolDef.mcpConfigName!
          })
      },
      ...(configs || []).map((item) => ({
        key: item.id,
        label: item.name,
        value: item.id,
        onClick: () => onMcpSettingsSave?.(toolIndex, item.id)
      }))
    ];

    const triggerLabel = selectedConfig ? selectedConfig.name : "Choose configuration";

    return (
      <div className="ai-agent-params__mcp-config-inline">
        <span className="ai-agent-params__mcp-config-label">Configuration</span>
        <KuikaDropdownMenu
          options={configOptions}
          selected={currentSettingsId ?? null}
          trigger="click"
          placement="bottomLeft"
          getPopupContainer={() => document.body}
          overlayClassName="ai-agent-params__dropdown-menu"
        >
          <button type="button" className="ai-agent-params__agent-input ai-agent-params__dropdown-trigger">
            <span className="ai-agent-params__dropdown-trigger-label">{triggerLabel}</span>
            <KuikaSvg type="arrows" name="chevron-down" width={12} height={12} color="var(--dark-6)" />
          </button>
        </KuikaDropdownMenu>
      </div>
    );
  };

  renderTools = () => {
    const { selectedAgent, viewMode, activeMcpToolIndex } = this.props;
    // KOA-16736: iterate over UNFILTERED tools so `index` matches the position in
    // data.metadata.tools — this is what `activeMcpToolIndex` and the parent's
    // handleSidebarMcpSettingsSave use. KAction tools are skipped during render.
    const tools = selectedAgent?.tools || [];
    const visibleToolCount = tools.filter((t) => t.toolType !== EAgentToolType.KAction).length;
    const isEditable = viewMode === "edit";
    const availableTools = this.getAvailableTools();

    return (
      <>
        {visibleToolCount === 0 && (
          <KuikaEmptyState
            svgName="wrench"
            customName="No tools assigned"
            description="Tools let this agent search the web, run code, or write files."
          />
        )}
        {visibleToolCount > 0 && (
          <div className="ai-agent-params__thread-root">
            {tools.map((tool, index) => {
              if (tool.toolType === EAgentToolType.KAction) return null;
              const toolDef = AGENT_TOOLS.find((t) => t.name === tool.name);
              const isMcpActive = activeMcpToolIndex === index && !!toolDef?.mcpConfigName;
              return (
                <div key={index} className="ai-agent-params__thread-item ai-agent-params__thread--depth-0">
                  <div className="ai-agent-params__thread-row">
                    <div className="ai-agent-params__thread-row-left">
                      <span className="ai-agent-params__thread-name">{toolDef?.displayName || tool.name}</span>
                    </div>
                    <div className="ai-agent-params__thread-actions">
                      <KuikaBadge content={toolDef?.group || "Tool"} color="purple" />
                      {isEditable && !!toolDef?.mcpConfigName && (
                        <KuikaButton
                          type="tertiary"
                          size="sm"
                          isIcon
                          onClick={() => this.props.onOpenMcpConfig?.(index)}
                        >
                          <KuikaSvg
                            type="sidebar-icons"
                            name="settings"
                            width={14}
                            height={14}
                            color={isMcpActive ? "var(--blue-6)" : "var(--dark-7)"}
                          />
                        </KuikaButton>
                      )}
                    </div>
                  </div>
                  {isMcpActive && this.renderMcpConfigInline(tool, index)}
                </div>
              );
            })}
          </div>
        )}
      </>
    );
  };

  // KOA-16736: KAction tools — "Actions" accordion content.
  // Plain list of icon + formatted name + category badge. Rows are NOT clickable
  // here — the detail view is opened by clicking the KAction tool node on the
  // workflow canvas (which triggers the prop `activeKActionId`, handled at the
  // top level of render() via renderKActionDetailView, not inside the accordion).
  renderActions = () => {
    const { selectedAgent } = this.props;
    const allTools = selectedAgent?.tools || [];
    const kActionTools = allTools.filter((t) => t.toolType === EAgentToolType.KAction);

    return (
      <>
        {kActionTools.length === 0 && (
          <KuikaEmptyState
            svgName="ai-action"
            svgType="datasource-icons"
            customName="No actions assigned"
            description="Actions let this agent call your app's database queries, APIs, and custom functions."
          />
        )}
        {kActionTools.length > 0 && (
          <div className="ai-agent-params__thread-root">
            {kActionTools.map((tool, index) => {
              const optionMeta = (this.props.kActionOptions || []).find((o) => o.actionId === tool.actionId);
              const badge = getActionCategoryBadge(optionMeta?.actionCategory, optionMeta?.actionType);
              return (
                <div
                  key={tool.actionId || index}
                  className="ai-agent-params__thread-item ai-agent-params__thread--depth-0"
                >
                  <div className="ai-agent-params__thread-row">
                    <div
                      className="ai-agent-params__thread-row-left"
                      style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 6 }}
                    >
                      <KuikaSvg type="datasource-icons" name="ai-action" width={14} height={14} />
                      <span className="ai-agent-params__thread-name">{formatActionDisplayName(tool.name)}</span>
                    </div>
                    <div className="ai-agent-params__thread-actions">
                      <KuikaBadge content={badge.label} color={badge.color} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </>
    );
  };

  // KOA-16736: Top-level "alone" view shown when activeKActionId prop is set
  // (i.e., the user clicked a KAction tool node on the workflow canvas).
  // Mirrors renderMcpToolConfigView — replaces the entire sidebar content
  // with a focused detail view for one KAction tool.
  renderKActionDetailView = () => {
    const { selectedAgent, activeKActionId, viewMode, onCloseKActionDetail } = this.props;
    if (!activeKActionId || !selectedAgent) return null;
    const tool = (selectedAgent.tools || []).find(
      (t) => t.toolType === EAgentToolType.KAction && t.actionId === activeKActionId
    );
    if (!tool) return null;

    const isEditable = viewMode === "edit";
    const optionMeta = (this.props.kActionOptions || []).find((o) => o.actionId === tool.actionId);
    const badge = getActionCategoryBadge(optionMeta?.actionCategory, optionMeta?.actionType);
    const isSystemAction = optionMeta?.actionType === EDatasourceType.SystemCSFunctions;
    const draft = this.state.editingDescriptionDraft ?? tool.description ?? "";
    const isGenerating = !!this.state.isGeneratingDescription;
    const isSaving = !!this.state.isSavingDescription;
    const busy = isGenerating || isSaving;
    const wasGenerated = this.state.editingDescriptionDraft !== undefined && isGenerating === false && draft.length > 0;
    const isDirty = (this.state.editingDescriptionDraft ?? tool.description ?? "") !== (tool.description ?? "");
    // System action descriptions ship with the platform metadata — they're read-only.
    const canEditDescription = isEditable && !isSystemAction;

    return (
      <div className="ai-agent-params">
        <div className="ai-agent-params__action-detail">
          <div className="ai-agent-params__action-detail-header">
            <button
              type="button"
              className="ai-agent-params__action-detail-back"
              onClick={() => {
                this.resetEditorState();
                onCloseKActionDetail?.();
              }}
              aria-label="Back to actions list"
            >
              <KuikaSvg type="arrows" name="arrow-left" width={16} height={16} color="var(--dark-7)" />
            </button>
            <KuikaSvg type="datasource-icons" name="ai-action" width={20} height={20} />
            <div className="ai-agent-params__action-detail-title">{formatActionDisplayName(tool.name)}</div>
            <KuikaBadge content={badge.label} color={badge.color} />
          </div>

          <div className="ai-agent-params__action-detail-card">
            <div className="ai-agent-params__action-detail-card-header">
              <span className="ai-agent-params__action-detail-card-label">Description</span>
              {wasGenerated && this.state.editingDescriptionDraft !== tool.description && (
                <span className="ai-agent-params__action-detail-card-badge">
                  <KuikaSvg type="datasource-icons" name="sparkles" width={12} height={12} />
                  AI generated
                </span>
              )}
            </div>
            <textarea
              className="ai-agent-params__action-detail-input"
              value={draft}
              rows={4}
              disabled={!canEditDescription || busy}
              placeholder={
                isSystemAction
                  ? "System action — description ships with the platform and cannot be edited."
                  : "Describe what this action does — used by the agent to decide when to call it."
              }
              onChange={(e) => this.setState({ editingDescriptionDraft: e.target.value })}
            />
            <div className="ai-agent-params__action-detail-help">
              <KuikaSvg type="common" name="info-circle" width={12} height={12} color="var(--dark-6)" />
              <span>
                {isSystemAction
                  ? "System action descriptions are read-only."
                  : "This description will be saved and used in future agent runs. You can edit it anytime."}
              </span>
            </div>

            <div className="ai-agent-params__action-detail-card-actions">
              {canEditDescription && (
                <KuikaButton
                  type="tertiary"
                  size="sm"
                  disabled={busy || !tool.actionId}
                  onClick={() => this.handleGenerateDescription(tool)}
                >
                  {isGenerating ? (
                    <span>Generating…</span>
                  ) : (
                    <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <KuikaSvg type="datasource-icons" name="sparkles" width={12} height={12} />
                      Generate with AI
                    </span>
                  )}
                </KuikaButton>
              )}
              <div style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
                <KuikaButton
                  type="tertiary"
                  size="sm"
                  disabled={busy}
                  onClick={() => {
                    this.resetEditorState();
                    onCloseKActionDetail?.();
                  }}
                >
                  {canEditDescription ? "Cancel" : "Back"}
                </KuikaButton>
                {canEditDescription && (
                  <KuikaButton
                    type="primary"
                    size="sm"
                    disabled={busy || !tool.actionId || !isDirty}
                    onClick={() => this.handleSaveDescription(tool)}
                  >
                    {isSaving ? "Saving…" : "Save"}
                  </KuikaButton>
                )}
              </div>
            </div>
          </div>

          {isEditable && (
            <div className="ai-agent-params__action-detail-footer">
              <KuikaButton
                type="tertiary"
                size="sm"
                onClick={() => {
                  this.handleRemoveAction(tool);
                  this.resetEditorState();
                  onCloseKActionDetail?.();
                }}
              >
                <span style={{ display: "flex", alignItems: "center", gap: 4, color: "var(--red-6)" }}>
                  <KuikaSvg type="common" name="trash" width={12} height={12} color="var(--red-6)" />
                  Remove from agent
                </span>
              </KuikaButton>
            </div>
          )}
        </div>
      </div>
    );
  };

  resetEditorState = () => {
    this.setState({
      editingDescriptionDraft: undefined,
      isGeneratingDescription: false,
      isSavingDescription: false
    });
  };

  handleGenerateDescription = async (tool: ICrewAgentTool) => {
    if (!tool.actionId) return;
    this.setState({ isGeneratingDescription: true });
    try {
      const response = await DatasourceService.GenerateActionDescriptions({
        actionIds: [tool.actionId],
        includeParameterDescriptions: true
      });
      const result = response?.data?.results?.[0];
      if (result?.success && result.description) {
        this.setState({ editingDescriptionDraft: result.description });
      } else {
        KMainFunctions.displayWarningNotification(
          result?.errorMessage || "Could not generate a description for this action."
        );
      }
    } catch {
      // Axios error handler in service already notified the user
    } finally {
      this.setState({ isGeneratingDescription: false });
    }
  };

  handleSaveDescription = async (tool: ICrewAgentTool) => {
    const { selectedAgent, onAgentUpdate } = this.props;
    if (!tool.actionId || !selectedAgent || !onAgentUpdate) return;
    const description = (this.state.editingDescriptionDraft ?? tool.description ?? "").trim();

    this.setState({ isSavingDescription: true });
    try {
      const response = await DatasourceService.SaveActionDescriptions({
        actionId: tool.actionId,
        description
      });
      if (response?.data?.success) {
        // Update the local agent's tool entry so the change shows immediately
        const updatedAgent = _.cloneDeep(selectedAgent);
        updatedAgent.tools = (updatedAgent.tools || []).map((t) =>
          t.actionId === tool.actionId ? { ...t, description } : t
        );
        onAgentUpdate(updatedAgent);
        this.setState({ isSavingDescription: false, editingDescriptionDraft: undefined });
      } else {
        KMainFunctions.displayWarningNotification(response?.data?.message || "Failed to save description.");
        this.setState({ isSavingDescription: false });
      }
    } catch {
      this.setState({ isSavingDescription: false });
    }
  };

  handleAddAction = (action: IAgentToolOption) => {
    const { selectedAgent, onAgentUpdate } = this.props;
    if (!selectedAgent || !onAgentUpdate) return;

    const updatedAgent = _.cloneDeep(selectedAgent);
    const tools = updatedAgent.tools || [];
    tools.push({
      name: action.name,
      toolType: EAgentToolType.KAction,
      actionId: action.actionId,
      description: action.description
    });
    updatedAgent.tools = tools;
    onAgentUpdate(updatedAgent);
  };

  handleRemoveAction = (toolToRemove: ICrewAgentTool) => {
    const { selectedAgent, onAgentUpdate } = this.props;
    if (!selectedAgent || !onAgentUpdate) return;

    const updatedAgent = _.cloneDeep(selectedAgent);
    updatedAgent.tools = (updatedAgent.tools || []).filter((t) => t.actionId !== toolToRemove.actionId);
    onAgentUpdate(updatedAgent);
  };

  getAvailableActions = () => {
    const { selectedAgent, kActionOptions } = this.props;
    const agentActionIds = new Set(
      (selectedAgent?.tools || []).filter((t) => t.toolType === EAgentToolType.KAction).map((t) => t.actionId)
    );

    return (kActionOptions || [])
      .filter((action) => !agentActionIds.has(action.actionId))
      .map((action) => ({
        group: action.group,
        key: `kaction-${action.actionId}`,
        label: action.displayName || action.name,
        value: action.actionId!,
        icon: <KuikaSvg type="datasource-icons" name="ai-action" width={12} height={12} />,
        onClick: () => this.handleAddAction(action)
      }));
  };

  renderAgentInputs = () => {
    const agentInputs = this.getAgentSpecificInputs();

    if (agentInputs.length === 0) {
      return (
        <KuikaEmptyState
          svgName="field"
          customName="No inputs used by this agent"
          description="Reference inputs in the task description using {input_name} syntax."
        />
      );
    }

    return this.renderInputRows(agentInputs);
  };

  // Render task description with highlighted inputs
  renderHighlightedDescription = (description: string) => {
    if (!description) return null;

    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    const regex = /\{([^}]+)\}/g;
    let match;

    while ((match = regex.exec(description)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push(description.substring(lastIndex, match.index));
      }

      // Add the highlighted input
      parts.push(
        <strong key={match.index} className="ai-agent-params__highlighted-input">
          {match[0]}
        </strong>
      );

      lastIndex = regex.lastIndex;
    }

    // Add remaining text
    if (lastIndex < description.length) {
      parts.push(description.substring(lastIndex));
    }

    return <>{parts}</>;
  };

  // Add tool to agent
  handleAddTool = (tool: ICrewAgentTool) => {
    const { selectedAgent, onAgentUpdate } = this.props;
    if (!selectedAgent || !onAgentUpdate) return;

    const updatedAgent = _.cloneDeep(selectedAgent);
    const tools = updatedAgent.tools || [];
    tools.push(tool);
    updatedAgent.tools = tools;

    onAgentUpdate(updatedAgent);
  };

  // Remove tool from agent
  handleRemoveTool = (toolToRemove: ICrewAgentTool) => {
    const { selectedAgent, onAgentUpdate, onCloseMcpConfig } = this.props;
    if (!selectedAgent || !onAgentUpdate) return;

    const updatedAgent = _.cloneDeep(selectedAgent);
    const tools = updatedAgent.tools || [];
    updatedAgent.tools = tools.filter((t) => t.name !== toolToRemove.name);

    onCloseMcpConfig?.();
    onAgentUpdate(updatedAgent);
  };

  // Get available tools for dropdown (not already added)
  getAvailableTools = () => {
    const { selectedAgent } = this.props;
    const agentTools = selectedAgent?.tools || [];

    return AGENT_TOOLS.filter((tool) => !agentTools.find((t) => t.name === tool.name)).map((tool) => ({
      group: tool.group,
      key: tool.name,
      label: tool.displayName || tool.name,
      value: tool.name,
      icon: (
        <KuikaSvg
          type="agent-icons"
          name={tool.icon}
          width={12}
          height={12}
          useOriginalColors={!tool.icon.endsWith("-tool")}
        />
      ),
      onClick: () => this.handleAddTool(tool)
    }));
  };

  handleUpdateAgentField = (field: keyof ICrewAgent | string, value: any) => {
    const { selectedAgent, onAgentUpdate } = this.props;
    if (!selectedAgent || !onAgentUpdate) return;

    const updatedAgent = _.cloneDeep(selectedAgent);

    const ensureTask = () => {
      if (!updatedAgent.task) {
        updatedAgent.task = { description: "", expected_output: "", output_format: {} };
      }
    };

    if (field === "name" || field === "role" || field === "goal" || field === "backstory") {
      updatedAgent[field] = value;
    } else if (field === "model" || field === "functionCallingLlm") {
      (updatedAgent as any)[field] = value || undefined;
    } else if (field === "reasoning") {
      updatedAgent.reasoning = value;
    } else if (field === "memoryMode") {
      updatedAgent.memoryMode = value as ECrewAiAgentMemoryMode;
      // When switching away from Isolated, drop per-agent memoryOptions so the
      // DTO doesn't carry dead config (Shared uses crew-level options).
      if (value !== ECrewAiAgentMemoryMode.Isolated) {
        delete updatedAgent.memoryOptions;
      }
    } else if (field === "memoryOptions.embedder") {
      // value is a full ICrewAiModelConfig or undefined -- assigned on the
      // per-agent record and only relevant in Isolated mode.
      const embedder = value as ICrewAiModelConfig | undefined;
      if (embedder) {
        updatedAgent.memoryOptions = { ...(updatedAgent.memoryOptions || {}), embedder };
      } else if (updatedAgent.memoryOptions) {
        const { embedder: _omit, ...rest } = updatedAgent.memoryOptions;
        updatedAgent.memoryOptions = Object.keys(rest).length ? rest : undefined;
      }
    } else if (field === "task.description") {
      ensureTask();
      updatedAgent.task!.description = value;
    } else if (field === "task.expected_output") {
      ensureTask();
      updatedAgent.task!.expected_output = value;
    } else if (field === "task.guardrail") {
      ensureTask();
      updatedAgent.task!.guardrail = value;
    } else if (field === "task.guardrailMaxRetryLimit") {
      ensureTask();
      updatedAgent.task!.guardrailMaxRetryLimit = value;
    }

    onAgentUpdate(updatedAgent);
  };

  // Extract {token} references from a task description.
  extractInputRefs = (description?: string): string[] => {
    if (!description) return [];
    const matches = description.match(/\{([^}]+)\}/g) || [];
    return matches.map((match: string) => match.slice(1, -1));
  };

  // Sync inputs with references across every agent's task description. Called on
  // blur of a task description textarea. Adds any newly-referenced inputs and
  // removes inputs that are no longer referenced anywhere — except inputs the
  // user configured manually (non-empty default value) and the conversation
  // history reserved names (session_id, user_message).
  syncInputsFromDescription = (
    editedDescription: string,
    currentInputs: ICrewInput[],
    onInputsChange: (inputs: ICrewInput[]) => void,
    editedAgentName?: string
  ) => {
    const { agents, managerAgent, conversationHistoryEnabled } = this.props;

    const allRefs = new Set<string>();
    this.extractInputRefs(editedDescription).forEach((name) => allRefs.add(name.toLowerCase()));
    (agents || []).forEach((agent) => {
      if (editedAgentName && agent.name === editedAgentName) return;
      this.extractInputRefs(agent.task?.description).forEach((name) => allRefs.add(name.toLowerCase()));
    });
    if (managerAgent && managerAgent.name !== editedAgentName) {
      this.extractInputRefs(managerAgent.task?.description).forEach((name) => allRefs.add(name.toLowerCase()));
    }

    const protectedNames = new Set<string>();
    if (conversationHistoryEnabled) {
      protectedNames.add("session_id");
      protectedNames.add("user_message");
    }

    const keptInputs = currentInputs.filter((input) => {
      const name = input.parameter_name?.toLowerCase() ?? "";
      if (!name) return true;
      if (protectedNames.has(name)) return true;
      if (input.parameter_default_value) return true;
      return allRefs.has(name);
    });

    const finalInputs = [...keptInputs];
    const existingNames = new Set(finalInputs.map((input) => input.parameter_name?.toLowerCase() ?? ""));
    this.extractInputRefs(editedDescription).forEach((inputName) => {
      if (!existingNames.has(inputName.toLowerCase())) {
        finalInputs.push({ parameter_name: inputName, parameter_format: "str" });
        existingNames.add(inputName.toLowerCase());
      }
    });

    const hasChanged =
      finalInputs.length !== currentInputs.length ||
      finalInputs.some((input, i) => input.parameter_name !== currentInputs[i]?.parameter_name);
    if (hasChanged) {
      onInputsChange(finalInputs);
    }
  };

  // Helper to get model name from a model ID string
  getModelName = (model: any, modelList?: IAiModel[]): string => {
    if (!model) return "";
    const value = typeof model === "string" ? model : model.id || model.name || "";
    if (!value) return "";
    const list = modelList ?? this.state.llmModels;
    const valueLower = String(value).toLowerCase();
    const foundById = list?.find((m) => m.id.toLowerCase() === valueLower);
    if (foundById) return foundById.name;
    const foundByName = list?.find((m) => m.name.toLowerCase() === valueLower);
    if (foundByName) return foundByName.name;
    return typeof model === "string" ? model : "";
  };

  // Helper to get model id from a model ID string
  getModelId = (model: any, modelList?: IAiModel[]): string => {
    if (!model) return "";
    const value = typeof model === "string" ? model : model.id || model.name || "";
    if (!value) return "";
    const list = modelList ?? this.state.llmModels;
    const valueLower = String(value).toLowerCase();
    const foundById = list?.find((m) => m.id.toLowerCase() === valueLower);
    if (foundById) return foundById.id;
    const foundByName = list?.find((m) => m.name.toLowerCase() === valueLower);
    return foundByName ? foundByName.id : "";
  };

  // Renders the "Embedder Model" row. Same visual pattern as the Agent/Tool Calling
  // LLM dropdowns. `selectedEmbedder` is the currently persisted config (may be
  // undefined -> show the backend default label). `onSelect` receives a model id
  // (or empty string to clear).
  renderEmbedderSection = (
    selectedEmbedder: ICrewAiModelConfig | undefined,
    onSelect: (modelId: string) => void,
    isEditable: boolean,
    tooltip: string
  ) => {
    const resolved = this.resolveEmbedderModel(selectedEmbedder);
    const selectedValue = resolved?.id || "";
    const selectedLabel = resolved
      ? this.formatModelLabel(resolved.name)
      : selectedEmbedder?.name
        ? `${selectedEmbedder.name} (not available)`
        : AIAgentSidebar.DEFAULT_EMBEDDER_NAME;

    const isResolving = this.state.embedderModels === undefined && !!selectedEmbedder;

    return (
      <div className="ai-agent-params__agent-section ai-agent-params__agent-section--inline">
        <div className="ai-agent-params__agent-label">
          <span>Embedder Model</span>
          <KuikaTooltip content={tooltip} position="top">
            <span>
              <KuikaSvg type="common" name="info-circle" width={14} height={14} color="var(--dark-6)" />
            </span>
          </KuikaTooltip>
        </div>
        <div className="ai-agent-params__agent-control">
          {isResolving ? (
            <span className="ai-agent-inline-spinner" aria-label="Loading models" />
          ) : isEditable ? (
            this.renderDropdownField(
              selectedValue,
              this.state.embedderModels?.length ? selectedLabel : "Models not available",
              this.getModelDropdownOptions(selectedValue, selectedLabel, "Select a model", this.state.embedderModels),
              onSelect,
              !this.state.embedderModels?.length
            )
          ) : (
            <div className="ai-agent-params__agent-value">{selectedLabel}</div>
          )}
        </div>
      </div>
    );
  };

  renderManagerAgentInfo = () => {
    const { managerAgent, viewMode, processManagerReadOnly } = this.props;
    const isEditable = viewMode === "edit" && !processManagerReadOnly;
    const managerModelValue = managerAgent?.model || "";
    const managerModelValueLower = managerModelValue.toLowerCase();
    const resolvedModel = managerModelValue
      ? this.state.llmModels?.find((m) => m.id.toLowerCase() === managerModelValueLower) ||
        this.state.llmModels?.find((m) => m.name.toLowerCase() === managerModelValueLower)
      : undefined;
    const managerModelLabel = resolvedModel
      ? this.formatModelLabel(resolvedModel.name)
      : managerModelValue
        ? "Unknown Model"
        : "Select a model";
    const managerModelSelectValue = resolvedModel?.id || managerModelValue;
    const isResolvingManagerModel = this.state.llmModels === undefined && !!managerModelValue;

    const managerFcValue = managerAgent?.functionCallingLlm || "";
    const managerFcValueLower = managerFcValue.toLowerCase();
    const resolvedFc = managerFcValue
      ? this.state.fcLlmModels?.find((m) => m.id.toLowerCase() === managerFcValueLower) ||
        this.state.fcLlmModels?.find((m) => m.name.toLowerCase() === managerFcValueLower)
      : undefined;
    const defaultManagerFcModel = this.state.fcLlmModels?.find((m) => m.name === "gpt-5.4-nano");
    const managerFcLabel = resolvedFc
      ? this.formatModelLabel(resolvedFc.name)
      : managerFcValue
        ? "Unknown Model"
        : defaultManagerFcModel
          ? this.formatModelLabel(defaultManagerFcModel.name)
          : "Select a model";
    const managerFcSelectValue = resolvedFc?.id || managerFcValue || defaultManagerFcModel?.id || "";
    const isResolvingManagerFc = this.state.fcLlmModels === undefined && !!managerFcValue;

    return (
      <div className="ai-agent-params__agent-details">
        <div className="ai-agent-params__agent-section ai-agent-params__agent-section--inline">
          <div className="ai-agent-params__agent-label">Name</div>
          <div className="ai-agent-params__agent-control">
            {isEditable ? (
              <input
                className="ai-agent-params__agent-input"
                value={managerAgent?.name || ""}
                onChange={(e) => this.handleManagerAgentFieldChange("name", e.target.value)}
                placeholder="Manager Agent"
              />
            ) : (
              <div className="ai-agent-params__agent-value">{managerAgent?.name || "Manager Agent"}</div>
            )}
          </div>
        </div>
        <div className="ai-agent-params__agent-section ai-agent-params__agent-section--inline">
          <div className="ai-agent-params__agent-label">
            <span>Role</span>
            <KuikaTooltip
              content="The manager's orchestration role that defines how it coordinates sub-agents."
              position="top"
            >
              <span>
                <KuikaSvg type="common" name="info-circle" width={14} height={14} color="var(--dark-6)" />
              </span>
            </KuikaTooltip>
          </div>
          <div className="ai-agent-params__agent-control">
            {isEditable ? (
              <input
                className="ai-agent-params__agent-input"
                value={managerAgent?.role || ""}
                onChange={(e) => this.handleManagerAgentFieldChange("role", e.target.value)}
                placeholder="e.g., Hierarchical Crew Manager"
              />
            ) : (
              <div className="ai-agent-params__agent-value">{managerAgent?.role || "Not configured"}</div>
            )}
          </div>
        </div>
        <div className="ai-agent-params__agent-section ai-agent-params__agent-section--inline">
          <div className="ai-agent-params__agent-label">
            <span>Goal</span>
            <KuikaTooltip content="The primary coordination objective for the manager." position="top">
              <span style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "fit-content" }}>
                <KuikaSvg type="common" name="info-circle" width={14} height={14} color="var(--dark-6)" />
              </span>
            </KuikaTooltip>
          </div>
          <div className="ai-agent-params__agent-control">
            {isEditable ? (
              <textarea
                className="ai-agent-params__agent-textarea"
                value={managerAgent?.goal || ""}
                onChange={(e) => this.handleManagerAgentFieldChange("goal", e.target.value)}
                placeholder="e.g., Coordinate research, analysis, and delivery agents"
                rows={2}
              />
            ) : (
              <div className="ai-agent-params__agent-value ai-agent-params__agent-value--multiline">
                {managerAgent?.goal || "Not configured"}
              </div>
            )}
          </div>
        </div>
        <div className="ai-agent-params__agent-section ai-agent-params__agent-section--inline">
          <div className="ai-agent-params__agent-label">
            <span>Backstory</span>
            <KuikaTooltip
              content="Background context shaping how the manager delegates and coordinates."
              position="top"
            >
              <span>
                <KuikaSvg type="common" name="info-circle" width={14} height={14} color="var(--dark-6)" />
              </span>
            </KuikaTooltip>
          </div>
          <div className="ai-agent-params__agent-control">
            {isEditable ? (
              <textarea
                className="ai-agent-params__agent-textarea"
                value={managerAgent?.backstory || ""}
                onChange={(e) => this.handleManagerAgentFieldChange("backstory", e.target.value)}
                placeholder="e.g., Only assign other agents to do the task..."
                rows={2}
              />
            ) : (
              <div className="ai-agent-params__agent-value ai-agent-params__agent-value--multiline">
                {managerAgent?.backstory || "Not configured"}
              </div>
            )}
          </div>
        </div>

        <div className="ai-agent-params__agent-section ai-agent-params__agent-section--inline">
          <div className="ai-agent-params__agent-label">LLM Model</div>
          <div className="ai-agent-params__agent-control">
            {isResolvingManagerModel ? (
              <span className="ai-agent-inline-spinner" aria-label="Loading models" />
            ) : isEditable ? (
              this.renderDropdownField(
                managerModelSelectValue,
                this.state.llmModels?.length ? managerModelLabel || "Select a model" : "Models not available",
                this.getModelDropdownOptions(managerModelSelectValue, managerModelLabel, "Select a model"),
                (value) => this.handleManagerAgentFieldChange("model", value || undefined),
                !this.state.llmModels?.length
              )
            ) : (
              <div className="ai-agent-params__agent-value">
                {managerModelValue ? managerModelLabel : "Not configured"}
              </div>
            )}
          </div>
        </div>

        <div className="ai-agent-params__agent-section ai-agent-params__agent-section--inline">
          <div className="ai-agent-params__agent-label">
            <span>Tool Calling LLM</span>
            <KuikaTooltip
              content="Dedicated LLM used for tool/function calls. Typically a smaller, faster model (mini/nano/lite). Falls back to gpt-5.4-nano if not set."
              position="top"
            >
              <span>
                <KuikaSvg type="common" name="info-circle" width={14} height={14} color="var(--dark-6)" />
              </span>
            </KuikaTooltip>
          </div>
          <div className="ai-agent-params__agent-control">
            {isResolvingManagerFc ? (
              <span className="ai-agent-inline-spinner" aria-label="Loading models" />
            ) : isEditable ? (
              this.renderDropdownField(
                managerFcSelectValue,
                this.state.fcLlmModels?.length ? managerFcLabel || "Select a model" : "Models not available",
                this.getModelDropdownOptions(
                  managerFcSelectValue,
                  managerFcLabel,
                  "Select a model",
                  this.state.fcLlmModels
                ),
                (value) => this.handleManagerAgentFieldChange("functionCallingLlm", value || undefined),
                !this.state.fcLlmModels?.length
              )
            ) : (
              <div className="ai-agent-params__agent-value">{managerFcLabel}</div>
            )}
          </div>
        </div>

        <div className="ai-agent-params__agent-section ai-agent-params__agent-section--inline">
          <div className="ai-agent-params__agent-label">
            <span>Reasoning</span>
            <KuikaTooltip
              content="Enable chain-of-thought reasoning. The agent plans and reflects step-by-step before acting. Useful for complex tasks; increases latency and token usage."
              position="top"
            >
              <span>
                <KuikaSvg type="common" name="info-circle" width={14} height={14} color="var(--dark-6)" />
              </span>
            </KuikaTooltip>
          </div>
          <div className="ai-agent-params__agent-control">
            {isEditable ? (
              <KuikaSwitch
                checked={!!managerAgent?.reasoning}
                onChange={(checked) => this.handleManagerAgentFieldChange("reasoning", checked)}
              />
            ) : (
              <div className="ai-agent-params__agent-value">{managerAgent?.reasoning ? "Enabled" : "Disabled"}</div>
            )}
          </div>
        </div>

        <div className="ai-agent-params__agent-section ai-agent-params__agent-section--inline">
          <div className="ai-agent-params__agent-label">
            <span>Memory</span>
            <KuikaTooltip
              content="Enable shared crew memory so agents can share context across tasks and learn from previous runs."
              position="top"
            >
              <span>
                <KuikaSvg type="common" name="info-circle" width={14} height={14} color="var(--dark-6)" />
              </span>
            </KuikaTooltip>
          </div>
          <div className="ai-agent-params__agent-control">
            {isEditable ? (
              <KuikaSwitch
                checked={managerAgent?.memoryMode === ECrewAiAgentMemoryMode.Shared}
                onChange={(checked) =>
                  this.handleManagerAgentFieldChange(
                    "memoryMode",
                    checked ? ECrewAiAgentMemoryMode.Shared : ECrewAiAgentMemoryMode.Disabled
                  )
                }
              />
            ) : (
              <div className="ai-agent-params__agent-value">
                {managerAgent?.memoryMode === ECrewAiAgentMemoryMode.Shared ? "Enabled" : "Disabled"}
              </div>
            )}
          </div>
        </div>

        {/* Crew-level embedder selector. Only meaningful when crew memory is enabled
            (hierarchical manager Shared). Read from crew-level memoryOptions prop. */}
        {managerAgent?.memoryMode === ECrewAiAgentMemoryMode.Shared &&
          this.renderEmbedderSection(
            this.props.crewMemoryOptions?.embedder,
            this.handleCrewEmbedderChange,
            isEditable,
            "Embedding model used to encode crew memory for semantic retrieval. Defaults to text-embedding-3-small."
          )}
      </div>
    );
  };

  renderSelectedAgent = () => {
    const { selectedAgent, viewMode, processType } = this.props;
    const isEditable = viewMode === "edit";

    if (!selectedAgent) {
      return (
        <div className="ai-agent-params__empty-container">
          <span className="ai-agent-params__empty">Click on an agent node to view its properties</span>
        </div>
      );
    }

    const tools = selectedAgent.tools || [];
    const modelName = this.getModelName(selectedAgent.model) || "gpt-5.4";
    const modelId = this.getModelId(selectedAgent.model);
    const rawFcLlm = this.getModelId(selectedAgent.functionCallingLlm, this.state.fcLlmModels);
    const defaultFcModel = this.state.fcLlmModels?.find((m) => m.name === "gpt-5.4-nano");
    const fcLlmId = rawFcLlm || defaultFcModel?.id || "";
    const fcLlmName =
      this.getModelName(selectedAgent.functionCallingLlm, this.state.fcLlmModels) ||
      defaultFcModel?.name ||
      "gpt-5.4-nano";

    return (
      <div className="ai-agent-params__agent-details">
        <div className="ai-agent-params__agent-section ai-agent-params__agent-section--inline">
          <div className="ai-agent-params__agent-label">Name</div>
          <div className="ai-agent-params__agent-control">
            {isEditable ? (
              <input
                className="ai-agent-params__agent-input"
                value={selectedAgent.name}
                onChange={(e) => this.handleUpdateAgentField("name", e.target.value)}
                placeholder="Agent Name"
              />
            ) : (
              <div className="ai-agent-params__agent-value">{selectedAgent.name}</div>
            )}
          </div>
        </div>
        <div className="ai-agent-params__agent-section ai-agent-params__agent-section--inline">
          <div className="ai-agent-params__agent-label">
            <span>Role</span>
            <KuikaTooltip content="The agent's job title or function that defines its expertise area." position="top">
              <span>
                <KuikaSvg type="common" name="info-circle" width={14} height={14} color="var(--dark-6)" />
              </span>
            </KuikaTooltip>
          </div>
          <div className="ai-agent-params__agent-control">
            {isEditable ? (
              <input
                className="ai-agent-params__agent-input"
                value={selectedAgent.role || ""}
                onChange={(e) => this.handleUpdateAgentField("role", e.target.value)}
                placeholder="e.g., Financial Analyst"
              />
            ) : (
              <div className="ai-agent-params__agent-value">{selectedAgent.role || "Not configured"}</div>
            )}
          </div>
        </div>
        <div className="ai-agent-params__agent-section ai-agent-params__agent-section--inline">
          <div className="ai-agent-params__agent-label">
            <span>Goal</span>
            <KuikaTooltip content="The primary objective this agent is trying to achieve." position="top">
              <span>
                <KuikaSvg type="common" name="info-circle" width={14} height={14} color="var(--dark-6)" />
              </span>
            </KuikaTooltip>
          </div>
          <div className="ai-agent-params__agent-control">
            {isEditable ? (
              <textarea
                className="ai-agent-params__agent-textarea"
                value={selectedAgent.goal || ""}
                onChange={(e) => this.handleUpdateAgentField("goal", e.target.value)}
                placeholder="e.g., Analyze financial data and produce insights"
                rows={2}
              />
            ) : (
              <div className="ai-agent-params__agent-value ai-agent-params__agent-value--multiline">
                {selectedAgent.goal || "Not configured"}
              </div>
            )}
          </div>
        </div>
        <div className="ai-agent-params__agent-section ai-agent-params__agent-section--inline">
          <div className="ai-agent-params__agent-label">
            <span>Backstory</span>
            <KuikaTooltip content="Background context that shapes how the agent approaches its tasks." position="top">
              <span>
                <KuikaSvg type="common" name="info-circle" width={14} height={14} color="var(--dark-6)" />
              </span>
            </KuikaTooltip>
          </div>
          <div className="ai-agent-params__agent-control">
            {isEditable ? (
              <textarea
                className="ai-agent-params__agent-textarea"
                value={selectedAgent.backstory || ""}
                onChange={(e) => this.handleUpdateAgentField("backstory", e.target.value)}
                placeholder="e.g., With 10 years of experience in data analysis..."
                rows={2}
              />
            ) : (
              <div className="ai-agent-params__agent-value ai-agent-params__agent-value--multiline">
                {selectedAgent.backstory || "Not configured"}
              </div>
            )}
          </div>
        </div>
        <div className="ai-agent-params__agent-section ai-agent-params__agent-section--inline">
          <div className="ai-agent-params__agent-label">LLM Model</div>
          <div className="ai-agent-params__agent-control">
            {this.state.llmModels === undefined && (modelId || modelName) ? (
              <span className="ai-agent-inline-spinner" aria-label="Loading models" />
            ) : isEditable ? (
              this.renderDropdownField(
                modelId,
                this.state.llmModels?.length
                  ? modelId
                    ? this.formatModelLabel(modelName)
                    : "Select a model"
                  : "Models not available",
                this.getModelDropdownOptions(modelId, this.formatModelLabel(modelName), "Select a model"),
                (value) => this.handleUpdateAgentField("model", value),
                !this.state.llmModels?.length
              )
            ) : (
              <div className="ai-agent-params__agent-value">
                {(() => {
                  const foundModel = this.state.llmModels?.find((m) => m.id === modelId);
                  return foundModel ? this.formatModelLabel(foundModel.name) : modelName || "No model selected";
                })()}
              </div>
            )}
          </div>
        </div>
        <div className="ai-agent-params__agent-section ai-agent-params__agent-section--inline">
          <div className="ai-agent-params__agent-label">
            <span>Tool Calling LLM</span>
            <KuikaTooltip
              content="Dedicated LLM used for tool/function calls. Typically a smaller, faster model (mini/nano/lite). Falls back to gpt-5.4-nano if not set."
              position="top"
            >
              <span>
                <KuikaSvg type="common" name="info-circle" width={14} height={14} color="var(--dark-6)" />
              </span>
            </KuikaTooltip>
          </div>
          <div className="ai-agent-params__agent-control">
            {this.state.fcLlmModels === undefined && (fcLlmId || fcLlmName) ? (
              <span className="ai-agent-inline-spinner" aria-label="Loading models" />
            ) : isEditable ? (
              this.renderDropdownField(
                fcLlmId,
                this.state.fcLlmModels?.length
                  ? fcLlmId
                    ? this.formatModelLabel(fcLlmName)
                    : "Select a model"
                  : "Models not available",
                this.getModelDropdownOptions(
                  fcLlmId,
                  this.formatModelLabel(fcLlmName),
                  "Select a model",
                  this.state.fcLlmModels
                ),
                (value) => this.handleUpdateAgentField("functionCallingLlm", value),
                !this.state.fcLlmModels?.length
              )
            ) : (
              <div className="ai-agent-params__agent-value">
                {(() => {
                  const foundModel = this.state.fcLlmModels?.find((m) => m.id === fcLlmId);
                  return foundModel ? this.formatModelLabel(foundModel.name) : fcLlmName || "No model selected";
                })()}
              </div>
            )}
          </div>
        </div>
        <div className="ai-agent-params__agent-section ai-agent-params__agent-section--inline">
          <div className="ai-agent-params__agent-label">
            <span>Reasoning</span>
            <KuikaTooltip
              content="Enable chain-of-thought reasoning. The agent plans and reflects step-by-step before acting. Useful for complex tasks; increases latency and token usage."
              position="top"
            >
              <span>
                <KuikaSvg type="common" name="info-circle" width={14} height={14} color="var(--dark-6)" />
              </span>
            </KuikaTooltip>
          </div>
          <div className="ai-agent-params__agent-control">
            {isEditable ? (
              <KuikaSwitch
                checked={!!selectedAgent.reasoning}
                onChange={(checked) => this.handleUpdateAgentField("reasoning", checked)}
              />
            ) : (
              <div className="ai-agent-params__agent-value">{selectedAgent.reasoning ? "Enabled" : "Disabled"}</div>
            )}
          </div>
        </div>
        {/* KOA-14774: memory is crew-level and manager-only in hierarchical mode.
            Only render per-agent memory in sequential mode. */}
        {processType !== "hierarchical" && (
          <>
            <div className="ai-agent-params__agent-section ai-agent-params__agent-section--inline">
              <div className="ai-agent-params__agent-label">Memory</div>
              <div className="ai-agent-params__agent-control">
                {isEditable ? (
                  this.renderDropdownField(
                    String(selectedAgent.memoryMode ?? ECrewAiAgentMemoryMode.Disabled),
                    this.memoryModeLabel(selectedAgent.memoryMode),
                    this.getMemoryModeDropdownOptions(),
                    (value) => this.handleUpdateAgentField("memoryMode", parseInt(value, 10) as ECrewAiAgentMemoryMode)
                  )
                ) : (
                  <div className="ai-agent-params__agent-value">{this.memoryModeLabel(selectedAgent.memoryMode)}</div>
                )}
              </div>
            </div>

            {/* Shared mode -> shows the crew-level embedder (same value across all
                Shared agents). Isolated mode -> edits this agent's own embedder. */}
            {selectedAgent.memoryMode === ECrewAiAgentMemoryMode.Shared &&
              this.renderEmbedderSection(
                this.props.crewMemoryOptions?.embedder,
                this.handleCrewEmbedderChange,
                isEditable,
                "Embedding model used for the crew's shared memory. This value is shared across every agent using Shared mode."
              )}
            {selectedAgent.memoryMode === ECrewAiAgentMemoryMode.Isolated &&
              this.renderEmbedderSection(
                selectedAgent.memoryOptions?.embedder,
                (modelId) => {
                  const embedder = modelId ? this.buildEmbedderConfigFromModelId(modelId) : undefined;
                  this.handleUpdateAgentField("memoryOptions.embedder", embedder);
                },
                isEditable,
                "Embedding model for this agent's isolated memory store. Applies only to this agent."
              )}
          </>
        )}
      </div>
    );
  };

  // Renders task description + guardrail fields for the manager agent in hierarchical mode.
  // Reads from `this.props.managerAgent.task` (the manager's own task) rather than
  // `selectedAgent.task`, since the manager is not a node in state.nodes under KOA-14774.
  renderManagerTaskParams = () => {
    const { managerAgent, viewMode, processManagerReadOnly } = this.props;
    const isEditable = viewMode === "edit" && !processManagerReadOnly;
    const task = managerAgent?.task;
    const guardrail = task?.guardrail || "";
    const guardrailMaxRetryLimit = task?.guardrailMaxRetryLimit ?? 3;

    return (
      <div className="ai-agent-params__section">
        <div className="ai-agent-params__agent-section">
          <div className="ai-agent-params__agent-label">Task Description</div>
          <div className="ai-agent-params__agent-control">
            {isEditable ? (
              <textarea
                className="ai-agent-params__agent-textarea"
                value={task?.description || ""}
                onChange={(e) => this.handleManagerAgentFieldChange("task.description", e.target.value)}
                placeholder="Describe the overall workflow the manager should coordinate..."
                rows={4}
              />
            ) : (
              <div className="ai-agent-params__agent-value ai-agent-params__agent-value--multiline">
                {task?.description || "Not configured"}
              </div>
            )}
          </div>
        </div>

        {/* <div className="ai-agent-params__agent-section">
          <div className="ai-agent-params__agent-label">Expected Output</div>
          <div className="ai-agent-params__agent-control">
            {isEditable ? (
              <textarea
                className="ai-agent-params__agent-textarea"
                value={task?.expected_output || ""}
                onChange={(e) => this.handleManagerAgentFieldChange("task.expected_output", e.target.value)}
                placeholder="What should the final output look like..."
                rows={3}
              />
            ) : (
              <div className="ai-agent-params__agent-value ai-agent-params__agent-value--multiline">
                {task?.expected_output || "Not configured"}
              </div>
            )}
          </div>
        </div> */}

        <div className="ai-agent-params__agent-section">
          <div className="ai-agent-params__agent-label">
            <span>Guardrail</span>
            <KuikaTooltip
              content="Natural language validation rule applied to the final output. If validation fails, the manager retries up to the max retry limit."
              position="top"
            >
              <span>
                <KuikaSvg type="common" name="info-circle" width={14} height={14} color="var(--dark-6)" />
              </span>
            </KuikaTooltip>
          </div>
          <div className="ai-agent-params__agent-control">
            {isEditable ? (
              <textarea
                className="ai-agent-params__agent-textarea"
                value={guardrail}
                onChange={(e) => this.handleManagerAgentFieldChange("task.guardrail", e.target.value || undefined)}
                placeholder="e.g., 'Ensure the final response is grounded in evidence and ready to deliver'"
                rows={3}
              />
            ) : (
              <div className="ai-agent-params__agent-value">{guardrail || "Not configured"}</div>
            )}
          </div>
        </div>

        <div className="ai-agent-params__agent-section ai-agent-params__agent-section--inline">
          <div className="ai-agent-params__agent-label">
            <span>Guardrail Max Retries</span>
            <KuikaTooltip
              content="Maximum number of times the manager will retry if guardrail validation fails. Default is 3."
              position="top"
            >
              <span>
                <KuikaSvg type="common" name="info-circle" width={14} height={14} color="var(--dark-6)" />
              </span>
            </KuikaTooltip>
          </div>
          <div className="ai-agent-params__agent-control">
            {isEditable ? (
              <input
                type="number"
                className="ai-agent-params__agent-input"
                value={guardrailMaxRetryLimit}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  this.handleManagerAgentFieldChange("task.guardrailMaxRetryLimit", isNaN(value) ? 3 : value);
                }}
                min={1}
                max={10}
              />
            ) : (
              <div className="ai-agent-params__agent-value">{guardrailMaxRetryLimit}</div>
            )}
          </div>
        </div>
      </div>
    );
  };

  renderAgentParams = () => {
    const { selectedAgent, viewMode } = this.props;
    const isEditable = viewMode === "edit";

    if (!selectedAgent?.task) {
      return (
        <div className="ai-agent-params__empty-container">
          <span className="ai-agent-params__empty">No parameters configured</span>
        </div>
      );
    }

    const task = selectedAgent.task;
    const guardrail = task.guardrail;
    const guardrailMaxRetryLimit = task.guardrailMaxRetryLimit ?? 3;

    const { processType } = this.props;

    return (
      <div className="ai-agent-params__section">
        {(processType !== "hierarchical" || this.props.isManagerMode) && (
          <div className="ai-agent-params__agent-section">
            <div className="ai-agent-params__agent-label">Task Description</div>
            <div className="ai-agent-params__agent-control">
              {isEditable ? (
                <textarea
                  className="ai-agent-params__agent-textarea"
                  value={task.description || ""}
                  onChange={(e) => this.handleUpdateAgentField("task.description", e.target.value)}
                  onBlur={(e) => {
                    const { inputs, onInputsChange, selectedAgent } = this.props;
                    if (onInputsChange) {
                      this.syncInputsFromDescription(e.target.value, inputs || [], onInputsChange, selectedAgent?.name);
                    }
                  }}
                  placeholder="Task Description (use {inputName} to reference inputs)"
                  rows={5}
                />
              ) : (
                <div className="ai-agent-params__agent-value ai-agent-params__agent-value--multiline">
                  {task.description ? this.renderHighlightedDescription(task.description) : "Not configured"}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="ai-agent-params__agent-section">
          <div className="ai-agent-params__agent-label">
            <span>Guardrail</span>
            <KuikaTooltip
              content="Natural language validation rule applied to agent output. If validation fails, the agent retries up to the max retry limit."
              position="top"
            >
              <span>
                <KuikaSvg type="common" name="info-circle" width={14} height={14} color="var(--dark-6)" />
              </span>
            </KuikaTooltip>
          </div>
          <div className="ai-agent-params__agent-control">
            {isEditable ? (
              <textarea
                className="ai-agent-params__agent-textarea"
                value={guardrail || ""}
                onChange={(e) => this.handleUpdateAgentField("task.guardrail", e.target.value || undefined)}
                placeholder="e.g., 'Ensure the response is professional and contains no offensive language'"
                rows={3}
              />
            ) : (
              <div className="ai-agent-params__agent-value">{guardrail || "Not configured"}</div>
            )}
          </div>
        </div>

        <div className="ai-agent-params__agent-section ai-agent-params__agent-section--inline">
          <div className="ai-agent-params__agent-label">
            <span>Guardrail Max Retry Limit</span>
            <KuikaTooltip
              content="Maximum number of times the agent will retry if guardrail validation fails. Default is 3."
              position="top"
            >
              <span>
                <KuikaSvg type="common" name="info-circle" width={14} height={14} color="var(--dark-6)" />
              </span>
            </KuikaTooltip>
          </div>
          <div className="ai-agent-params__agent-control">
            {isEditable ? (
              <input
                type="number"
                className="ai-agent-params__agent-input"
                value={guardrailMaxRetryLimit}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  this.handleUpdateAgentField("task.guardrailMaxRetryLimit", isNaN(value) ? 3 : value);
                }}
                min={1}
                max={10}
              />
            ) : (
              <div className="ai-agent-params__agent-value">{guardrailMaxRetryLimit}</div>
            )}
          </div>
        </div>
      </div>
    );
  };

  render() {
    const {
      viewMode,
      selectedAgent,
      activeMcpToolIndex,
      onOpenInputsModal,
      onOpenCustomTypesModal,
      onClearSelection,
      onEditAgentName,
      onRemoveAgent
    } = this.props;

    // Show dedicated MCP tool config view when a tool config is being configured
    if (activeMcpToolIndex != null && selectedAgent) {
      return this.renderMcpToolConfigView() ?? <div className="ai-agent-params" />;
    }

    // KOA-16736: Show dedicated KAction detail view when a KAction tool node was
    // clicked on the workflow canvas. Replaces the entire sidebar (alone view).
    if (this.props.activeKActionId && selectedAgent) {
      return this.renderKActionDetailView() ?? <div className="ai-agent-params" />;
    }

    const isEditable = viewMode === "edit";

    // Show manager agent info when manager mode is active
    if (this.props.isManagerMode) {
      return (
        <div className="ai-agent-params">
          <KuikaCollapsePanel
            animated={true}
            activeKey={this.state.activeAccordionKeys}
            onChange={(keys) => {
              const keyArray = Array.isArray(keys) ? keys : [keys];
              this.setState({ activeAccordionKeys: keyArray.slice(-1) as string[] });
            }}
            className="ai-agent-params__accordion"
            iconPlacement="right"
            items={[
              {
                key: "managerInfo",
                label: (
                  <div className="ai-agent-params__accordion-header">
                    <span className="ai-agent-params__accordion-label">Manager Agent Info</span>
                  </div>
                ),
                children: this.renderManagerAgentInfo()
              },
              {
                key: "agentParams",
                label: (
                  <div className="ai-agent-params__accordion-header">
                    <span className="ai-agent-params__accordion-label">Task Params</span>
                  </div>
                ),
                children: this.renderManagerTaskParams()
              },
              {
                key: "returnTypes",
                label: (
                  <div className="ai-agent-params__accordion-header">
                    <span className="ai-agent-params__accordion-label">Return Types</span>
                    {isEditable && this.state.activeAccordionKeys.includes("returnTypes") && (
                      <KuikaButton
                        type="tertiary"
                        size="sm"
                        isIcon
                        onClick={(e) => {
                          e.stopPropagation();
                          this.props.onOpenManagerResponseModal?.();
                        }}
                      >
                        <KuikaSvg type="common" name="edit" width={14} height={14} />
                      </KuikaButton>
                    )}
                  </div>
                ),
                children: this.renderReturnTypes(this.props.managerAgent)
              },
              // {
              //   key: "inputs",
              //   label: (
              //     <div className="ai-agent-params__accordion-header">
              //       <span className="ai-agent-params__accordion-label">Inputs</span>
              //       {isEditable && this.state.activeAccordionKeys.includes("inputs") && (
              //         <KuikaButton
              //           type="tertiary"
              //           size="sm"
              //           isIcon
              //           onClick={(e) => {
              //             e.stopPropagation();
              //             this.props.onOpenInputsModal?.();
              //           }}
              //         >
              //           <KuikaSvg type="common" name="edit" width={14} height={14} />
              //         </KuikaButton>
              //       )}
              //     </div>
              //   ),
              //   children: this.renderInputs()
              // },
              {
                key: "customTypes",
                label: (
                  <div className="ai-agent-params__accordion-header">
                    <span className="ai-agent-params__accordion-label">Custom Data Types</span>
                    {isEditable && this.state.activeAccordionKeys.includes("customTypes") && (
                      <KuikaButton
                        type="tertiary"
                        size="sm"
                        isIcon
                        onClick={(e) => {
                          e.stopPropagation();
                          this.props.onOpenCustomTypesModal?.();
                        }}
                      >
                        <KuikaSvg type="common" name="edit" width={14} height={14} />
                      </KuikaButton>
                    )}
                  </div>
                ),
                children: this.renderCustomDataTypes()
              }
            ]}
          />
        </div>
      );
    }

    // Show agent-specific info when agent is selected
    if (selectedAgent) {
      return (
        <div className="ai-agent-params">
          <KuikaCollapsePanel
            animated={true}
            activeKey={this.state.activeAccordionKeys}
            onChange={(keys) => {
              // Only allow one section open at a time
              const keyArray = Array.isArray(keys) ? keys : [keys];
              this.setState({ activeAccordionKeys: keyArray.slice(-1) as string[] });
            }}
            className="ai-agent-params__accordion"
            iconPlacement="right"
            items={[
              {
                key: "selectedAgent",
                label: (
                  <div className="ai-agent-params__accordion-header">
                    <span className="ai-agent-params__accordion-label">Agent Info</span>
                  </div>
                ),
                children: this.renderSelectedAgent()
              },
              {
                key: "tools",
                label: (
                  <div className="ai-agent-params__accordion-header">
                    <span className="ai-agent-params__accordion-label">Tools</span>
                    {isEditable &&
                      this.state.activeAccordionKeys.includes("tools") &&
                      this.getAvailableTools().length > 0 && (
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          {/* <KuikaDropdownMenu options={this.getAvailableTools()} placement="bottomRight">
                            <KuikaButton type="tertiary" size="sm" isIcon>
                              <KuikaTooltip content="Add Tool" position="top">
                                <span>
                                  <KuikaSvg type="common" name="plus" width={14} height={14} />
                                </span>
                              </KuikaTooltip>
                            </KuikaButton>
                          </KuikaDropdownMenu> */}
                        </div>
                      )}
                  </div>
                ),
                children: this.renderTools()
              },
              {
                key: "actions",
                label: (
                  <div className="ai-agent-params__accordion-header">
                    <span className="ai-agent-params__accordion-label">Actions</span>
                    {isEditable &&
                      this.state.activeAccordionKeys.includes("actions") &&
                      this.getAvailableActions().length > 0 && (
                        <div onClick={(e) => e.stopPropagation()}>
                          <KuikaDropdownMenu options={this.getAvailableActions()} placement="bottomRight">
                            <KuikaButton type="tertiary" size="sm" isIcon>
                              <KuikaTooltip content="Add Action" position="top">
                                <span>
                                  <KuikaSvg type="common" name="plus" width={14} height={14} />
                                </span>
                              </KuikaTooltip>
                            </KuikaButton>
                          </KuikaDropdownMenu>
                        </div>
                      )}
                  </div>
                ),
                children: this.renderActions()
              },
              ...(this.props.processType !== "hierarchical"
                ? [
                    {
                      key: "agentParams",
                      label: (
                        <div className="ai-agent-params__accordion-header">
                          <span className="ai-agent-params__accordion-label">Task Parameters</span>
                        </div>
                      ),
                      children: this.renderAgentParams()
                    },
                    {
                      key: "returnTypes",
                      label: (
                        <div className="ai-agent-params__accordion-header">
                          <span className="ai-agent-params__accordion-label">Return Types</span>
                          {isEditable && this.state.activeAccordionKeys.includes("returnTypes") && (
                            <KuikaButton
                              type="tertiary"
                              size="sm"
                              isIcon
                              onClick={(e) => {
                                e.stopPropagation();
                                this.props.onOpenResponseModal?.();
                              }}
                            >
                              <KuikaSvg type="common" name="edit" width={14} height={14} />
                            </KuikaButton>
                          )}
                        </div>
                      ),
                      children: this.renderReturnTypes()
                    }
                  ]
                : [])
            ]}
          />
        </div>
      );
    }

    // Show global inputs and custom types when no agent is selected
    const generalKeys = ["crewProcess", "inputs", "customTypes"];
    const activeGeneralKeys = this.state.activeAccordionKeys.filter((k) => generalKeys.includes(k));
    const effectiveActiveKey = activeGeneralKeys.length ? activeGeneralKeys : ["crewProcess"];
    return (
      <div className="ai-agent-params">
        <KuikaCollapsePanel
          animated={true}
          activeKey={effectiveActiveKey}
          onChange={(keys) => {
            // Only allow one section open at a time
            const keyArray = Array.isArray(keys) ? keys : [keys];
            this.setState({ activeAccordionKeys: keyArray.slice(-1) as string[] });
          }}
          className="ai-agent-params__accordion"
          iconPlacement="right"
          items={[
            {
              key: "crewProcess",
              label: (
                <div className="ai-agent-params__accordion-header">
                  <span className="ai-agent-params__accordion-label">Crew Process</span>
                </div>
              ),
              children: this.renderCrewProcessSettings()
            },
            {
              key: "inputs",
              label: (
                <div className="ai-agent-params__accordion-header">
                  <span className="ai-agent-params__accordion-label">Inputs</span>
                  {isEditable && onOpenInputsModal && this.state.activeAccordionKeys.includes("inputs") && (
                    <KuikaButton
                      type="tertiary"
                      size="sm"
                      isIcon
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenInputsModal();
                      }}
                    >
                      <KuikaSvg type="common" name="edit" width={14} height={14} />
                    </KuikaButton>
                  )}
                </div>
              ),
              children: this.renderInputs()
            },
            {
              key: "customTypes",
              label: (
                <div className="ai-agent-params__accordion-header">
                  <span className="ai-agent-params__accordion-label">Custom Data Types</span>
                  {isEditable && onOpenCustomTypesModal && this.state.activeAccordionKeys.includes("customTypes") && (
                    <KuikaButton
                      type="tertiary"
                      size="sm"
                      isIcon
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenCustomTypesModal();
                      }}
                    >
                      <KuikaSvg type="common" name="edit" width={14} height={14} />
                    </KuikaButton>
                  )}
                </div>
              ),
              children: this.renderCustomDataTypes()
            }
          ]}
        />
      </div>
    );
  }
}

export default AIAgentSidebar;
